// BilderAuswahlDialog.tsx — T-287: „Mit oder ohne Bilder? Wie soll ich das
// beurteilen können, ohne zu wissen, um welches Bild es sich handelt?"
//
// Statt einer Ja/Nein-Frage zeigt das Popup die Bilder aus dem Mailtext als
// Vorschau; jedes lässt sich einzeln an- oder abwählen (alle vorausgewählt).
// Eigenes kvp-rd-Popup über `ConfirmDialog` — kein Browser- oder Fluent-Dialog.
// ESC, Klick daneben und der Nein-Knopf liefern „keine Bilder".

import * as React from "react";
import { ConfirmDialog } from "../redesign/ConfirmDialog";
import { I } from "../redesign/kvpIcons";
import { imEigenenDialog } from "../redesign/bestaetigen/bestaetigen";
import { Z_LAYERS } from "../windowManager/zLayers";
import { REFINED } from "../risikoanalyseStep/tokens";
import { formatBytes } from "./composeSanitize";

export type BilderAuswahlOptionen<T> = {
    titel: string;
    /** Text über dem Raster (z. B. was mit den Datei-Anhängen passiert). */
    text?: React.ReactNode;
    bilder: T[];
    /** Lädt das Bild für die Vorschau — Graph oder Mail-Archiv. */
    ladeBild: (bild: T) => Promise<Blob>;
    /** Sprechender Name unter dem Bild. */
    beschrift: (bild: T) => string;
    groesse?: (bild: T) => number | null | undefined;
    /** Beschriftung des Nein-Knopfs („Ohne Bilder zuordnen" / „Abbrechen"). */
    nein: string;
};

type Vorschau = { status: "laedt" } | { status: "ok"; url: string } | { status: "fehler" };

export function BilderAuswahlDialog<T>({
    o,
    fertig,
}: {
    o: BilderAuswahlOptionen<T>;
    fertig: (auswahl: T[]) => void;
}): React.ReactElement {
    const [gewaehlt, setGewaehlt] = React.useState<Set<number>>(() => new Set(o.bilder.map((_, i) => i)));
    const [vorschau, setVorschau] = React.useState<Vorschau[]>(() => o.bilder.map(() => ({ status: "laedt" })));

    React.useEffect(() => {
        let aktiv = true;
        const urls: string[] = [];
        o.bilder.forEach((bild, i) => {
            o.ladeBild(bild).then(
                (blob) => {
                    if (!aktiv) return;
                    const url = URL.createObjectURL(blob);
                    urls.push(url);
                    setVorschau((v) => v.map((x, j) => (j === i ? { status: "ok", url } : x)));
                },
                () => {
                    if (!aktiv) return;
                    setVorschau((v) => v.map((x, j) => (j === i ? { status: "fehler" } : x)));
                },
            );
        });
        return () => {
            aktiv = false;
            // Nach dem Schließen eintreffende Blobs bekommen gar keine URL mehr.
            for (const url of urls) URL.revokeObjectURL(url);
            urls.length = 0;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const umschalten = (i: number) =>
        setGewaehlt((alt) => {
            const neu = new Set(alt);
            if (neu.has(i)) neu.delete(i);
            else neu.add(i);
            return neu;
        });

    const anzahl = gewaehlt.size;
    const auswahl = () => o.bilder.filter((_, i) => gewaehlt.has(i));
    const alle = anzahl === o.bilder.length;

    return (
        <ConfirmDialog
            open
            title={o.titel}
            message={
                <div>
                    {o.text != null && <div style={{ marginBottom: 10 }}>{o.text}</div>}
                    {o.bilder.length > 1 && (
                        <button
                            type="button"
                            onClick={() => setGewaehlt(alle ? new Set() : new Set(o.bilder.map((_, i) => i)))}
                            style={{
                                background: "none",
                                border: 0,
                                padding: 0,
                                marginBottom: 8,
                                color: REFINED.aub,
                                fontSize: REFINED.fs.xs,
                                fontWeight: 600,
                                cursor: "pointer",
                            }}
                        >
                            {alle ? "Keines auswählen" : "Alle auswählen"}
                        </button>
                    )}
                    <div
                        role="group"
                        aria-label="Bilder aus dem Mailtext"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(112px, 1fr))",
                            gap: 8,
                            maxHeight: "50vh",
                            overflowY: "auto",
                        }}
                    >
                        {o.bilder.map((bild, i) => {
                            const name = o.beschrift(bild);
                            const v = vorschau[i];
                            const an = gewaehlt.has(i);
                            const groesse = o.groesse?.(bild);
                            return (
                                <label
                                    key={i}
                                    data-testid="bild-kachel"
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 4,
                                        padding: 6,
                                        borderRadius: 10,
                                        border: `1px solid ${an ? REFINED.aub : REFINED.line}`,
                                        background: an ? REFINED.aub50 : REFINED.white,
                                        cursor: "pointer",
                                        minWidth: 0,
                                    }}
                                >
                                    <span style={{ position: "relative", display: "block", height: 84 }}>
                                        {v.status === "ok" ? (
                                            <img
                                                src={v.url}
                                                alt={name}
                                                style={{ width: "100%", height: 84, objectFit: "contain", display: "block" }}
                                            />
                                        ) : (
                                            <span
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    height: 84,
                                                    fontSize: REFINED.fs.xxs,
                                                    color: REFINED.ink2,
                                                    textAlign: "center",
                                                }}
                                            >
                                                {v.status === "laedt" ? "Vorschau lädt …" : "Keine Vorschau"}
                                            </span>
                                        )}
                                        <input
                                            type="checkbox"
                                            checked={an}
                                            onChange={() => umschalten(i)}
                                            aria-label={`${name} übernehmen`}
                                            style={{ position: "absolute", top: 2, left: 2, margin: 0 }}
                                        />
                                    </span>
                                    <span
                                        title={name}
                                        style={{
                                            fontSize: REFINED.fs.xxs,
                                            color: REFINED.ink,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {name}
                                    </span>
                                    {groesse != null && groesse > 0 && (
                                        <span style={{ fontSize: REFINED.fs.xxs, color: REFINED.ink3 }}>{formatBytes(groesse)}</span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </div>
            }
            confirmLabel={anzahl === 0 ? o.nein : anzahl === 1 ? "1 Bild übernehmen" : `${anzahl} Bilder übernehmen`}
            cancelLabel={anzahl === 0 ? null : o.nein}
            tone="primary"
            icon={I.warnung}
            zIndex={Z_LAYERS.windowConfirm}
            onConfirm={() => fertig(auswahl())}
            onCancel={() => fertig([])}
        />
    );
}

/** Öffnet die Auswahl und liefert die gewählten Bilder (`[]` = keine). */
export function waehleBilder<T>(o: BilderAuswahlOptionen<T>): Promise<T[]> {
    return imEigenenDialog<T[]>([], (fertig) => <BilderAuswahlDialog o={o} fertig={fertig} />);
}
